import moment from 'moment';

import { uiHelpers } from '../../../helpers';
import { DailyTicker } from '../../../shared/types';
import {
  PriceDataPoint,
  PriceDataPointWithPreviousClose,
} from '../../../types';
import { calculateMacd } from './defaultIndicatorParts';

export const getInitialData = (
  dataPoints,
  probabilityMap,
  graphColorStyle,
  zoneData = []
) => {
  let initialData = dataPoints
    .filter(Boolean) // fix? price can be null due to unknown reason
    .map((price, i) => {
      const {
        date,
        open,
        close,
        high,
        low,
        volume,
        turnover,
        total_no,
        shifted_total_no,
        top3concen,
        top5concen,
        top10concen,
        top15concen,
      } = price;
      return {
        ...probabilityMap[date],
        ...zoneData[date],
        date: moment.utc(date).toDate(),
        open,
        high,
        low,
        close,
        volume,
        turnover,
        total_no,
        shifted_total_no,
        top3concen,
        top5concen,
        top10concen,
        top15concen,
        stroke:
          (i === 0 && close >= open) ||
          (i > 0 && close > dataPoints[i - 1].close)
            ? uiHelpers.candleStickColorMap(graphColorStyle).up
            : uiHelpers.candleStickColorMap(graphColorStyle).down,
      };
    });

  // calculate rsi, ma
  initialData = calculateMacd(initialData);

  if (initialData.length === 1) {
    // a workaround: https://github.com/rrag/react-stockcharts/issues/393
    initialData = [
      { date: initialData[0].date },
      ...initialData,
      { date: initialData[0].date },
    ];
  }

  return initialData;
};

export const aggregateDailyCandles = (
  ticks: readonly DailyTicker[],
  unit: string
): readonly PriceDataPoint[] => {
  const dataPoints = ticks || [];

  // monthly and weekly data need client-side aggregation
  if (unit !== 'd' && unit !== 'minute') {
    const method = (() => {
      switch (unit) {
        case 'w':
        default: {
          return 'week';
        }
        case 'm': {
          return 'month';
        }
      }
    })();
    const _dataPoints: PriceDataPoint[] = [];
    let date,
      open,
      high,
      low,
      close,
      volume = 0,
      turnover = 0;
    for (let i = 0; i < dataPoints.length; ++i) {
      const dataPoint = dataPoints[i];
      if (date === undefined) {
        date = dataPoint.date;
        open = dataPoint.open;
        high = dataPoint.high;
        low = dataPoint.low;
      } else {
        high = Math.max(high, dataPoint.high);
        low = Math.min(low, dataPoint.low);
      }
      volume += dataPoint.volume;
      turnover += dataPoint.turnover;
      // check end
      if (
        i === dataPoints.length - 1 ||
        moment.utc(dataPoint.date)[method]() !==
          moment.utc(dataPoints[i + 1].date)[method]()
      ) {
        close = dataPoint.close;

        _dataPoints.push({
          date,
          open,
          high,
          low,
          close,
          volume,
          turnover,
        });

        date = undefined;
        volume = 0;
      }
    }
    return _dataPoints;
  }

  return dataPoints;
};

/**
 * Insert previousClose to each data point, doing this in-place for performance consideration.
 */
export const insertPreviousClose = (
  dataPoints: readonly PriceDataPoint[]
): readonly PriceDataPointWithPreviousClose[] => {
  const castedDataPoints = dataPoints as PriceDataPointWithPreviousClose[];
  let previousClose: number;
  for (const point of castedDataPoints) {
    point.previousClose = previousClose;
    previousClose = point.close;
  }
  return dataPoints;
};
